import { Box, ConditionWrapper, openSplitViewState, PageWrapper } from '@approvalmax/ui/src/components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useSelector } from 'modules/react-redux';
import moment from 'moment';
// FIXME: We can't import from other pages.
import { CompanyRequestListFilter } from 'pages/requestList/config';
import { memo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useMount, useUnmount } from 'react-use';
import { useBackHeader } from 'shared/components';
import { useDeleteRequest } from 'shared/data/webApp/v1';
import { useCloseSplitViewOnUnmount } from 'shared/hooks/useCloseSplitViewOnUnmount';
import { hasChangesState } from 'shared/states';
import { unsavedChangesCallbackState } from 'shared/states/unsavedChangesCallback';
import { getPath, Path } from 'urlBuilder';

import RequestForm from '../../containers/RequestForm';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import { usePageRedesign } from '../../hooks/usePageRedesign';
import { useRedirectDelegatedApproverDueToRestrictions } from '../../hooks/useRedirectDelegatedApproverDueToRestrictions';
import { getDocumentTitle, getRequest } from '../../selectors/pageSelectors';
import { isModifiedRequestState } from '../../states';
import { Popups } from '../Popups/Popups';
import { SplitViewWrapper } from '../SplitViewWrapper/SplitViewWrapper';
import { CardWrp, Root, StyledDefaultPageLayout, StyledRequestForm } from './Content.styles';

export const Content = memo(() => {
    const request = useSelector(getRequest);
    const documentTitle = useSelector(getDocumentTitle);

    const [isModifiedRequest, setIsModifiedRequest] = useAtom(isModifiedRequestState);
    const [hasChanges, setHasChanges] = useAtom(hasChangesState);
    const setUnsavedChangesCallbackState = useSetAtom(unsavedChangesCallbackState);
    const isOpenSplitView = useAtomValue(openSplitViewState);

    const { mutateAsync: deleteRequest } = useDeleteRequest();
    const { isPageRedesign } = usePageRedesign();

    useBreadcrumbs();
    useCloseSplitViewOnUnmount();
    useRedirectDelegatedApproverDueToRestrictions();

    useMount(() => {
        const isModified = !moment(request.createdDate)
            .milliseconds(0)
            .isSame(moment(request.modifiedDate).milliseconds(0));

        if (isModified) {
            setIsModifiedRequest(true);
        }
    });

    useUnmount(() => {
        setHasChanges(false);

        setUnsavedChangesCallbackState(null);
    });

    const preBackAction = useCallback(async () => {
        if (isModifiedRequest) return;

        if (hasChanges) {
            const callback = async (isDiscard: boolean) => {
                if (isDiscard) {
                    await deleteRequest({ body: { requestId: request.id, companyId: request.companyId } });
                } else {
                    setUnsavedChangesCallbackState(null);
                }
            };

            setUnsavedChangesCallbackState(() => callback);
        } else {
            await deleteRequest({ body: { requestId: request.id, companyId: request.companyId } });
        }
    }, [isModifiedRequest, hasChanges, deleteRequest, request.id, request.companyId, setUnsavedChangesCallbackState]);

    useBackHeader(
        getPath(Path.requestCompanyList, request.companyId, CompanyRequestListFilter.CompanyAll, {
            requestId: request.id,
        }),
        false,
        preBackAction
    );

    return (
        <ConditionWrapper
            condition={isPageRedesign}
            wrapperIf={(children) => (
                <PageWrapper>
                    <Helmet title={documentTitle} />

                    <PageWrapper.Content spacingTop={24}>{children}</PageWrapper.Content>
                </PageWrapper>
            )}
            wrapperElse={(children) => (
                <Root>
                    <StyledDefaultPageLayout documentTitle={documentTitle} fixedWidth>
                        {children}
                    </StyledDefaultPageLayout>
                </Root>
            )}
        >
            <SplitViewWrapper>
                <div id='requestCardWrapper'>
                    {isPageRedesign ? (
                        <Box spacing={isOpenSplitView ? '24' : '0'} minWidth='max-content'>
                            <RequestForm />
                        </Box>
                    ) : (
                        <CardWrp>
                            <StyledRequestForm />
                        </CardWrp>
                    )}
                </div>
            </SplitViewWrapper>

            <Popups />
        </ConditionWrapper>
    );
});

Content.displayName = 'Content';
